import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import ReactApexChart from 'react-apexcharts';
import reportService from '../../../services/reports/report.service';
import { ShiftReportDto } from '../../../types/report.types';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Table, Select, Tag, Button, Progress, Tooltip } from 'antd';
import { FilterOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { OverlayTrigger, Popover, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '../../../utils/errorHandler';
import { createPdf, exportToExcel, normalizeTurkishChars } from '../../../utils/exportUtils';
import FooterSection from '../../shared/components/footerSection';
import { registerLocale, setDefaultLocale } from "react-datepicker";
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import "react-datepicker/dist/react-datepicker.css";
import i18n from '../../../i18n/config';
import { PermissionType } from '../../../types/user.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
const { Option } = Select;

const ShiftReport = () => {
    const [reportData, setReportData] = useState<ShiftReportDto[]>([]);
    const [filteredData, setFilteredData] = useState<ShiftReportDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)));
    const [endDate, setEndDate] = useState(new Date());
    const [departments, setDepartments] = useState<string[]>([]);
    const [branches, setBranches] = useState<string[]>([]);
    const [users, setUsers] = useState<string[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>(undefined);
    const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [selectedUsersDetails, setSelectedUsersDetails] = useState<ShiftReportDto[]>([]);
    const [pageSize, setPageSize] = useState<number>(10);
    const [employeeMinutesCache, setEmployeeMinutesCache] = useState<Map<number, { lateTotal: number, earlyTotal: number, breakOverdraftTotal: number }>>(new Map());
    // Kıyaslama için state'ler
    const [selectedDepartmentsForComparison, setSelectedDepartmentsForComparison] = useState<string[]>([]);
    const [selectedBranchesForComparison, setSelectedBranchesForComparison] = useState<string[]>([]);
    const { t } = useTranslation();
    const { user } = useSelector((state: RootState) => state.auth);
    // Dakikaları saat ve dakika formatına çeviren yardımcı fonksiyon
    const formatMinutesToHoursAndMinutes = (minutes: number): string => {
        if (minutes === 0) return '0 dakika';

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        if (hours === 0) {
            return `${remainingMinutes} dakika`;
        } else if (remainingMinutes === 0) {
            return `${hours} saat`;
        } else {
            return `${hours} saat ${remainingMinutes} dakika`;
        }
    };

    const getDepartmentName = (departments: Array<{ id: number, name: string }>) => {
        if (!departments || departments.length === 0) return 'Belirtilmemiş';
        return departments.map(dept => dept.name).join(', ');
    };

    // Helper function to get branch name
    const getBranchName = (branches: Array<{ id: number, name: string }>) => {
        if (!branches || branches.length === 0) return 'Belirtilmemiş';
        return branches.map(branch => branch.name).join(', ');
    };

    useEffect(() => {
        // Türkçe ve İngilizce locale'leri kaydet
        registerLocale('tr', trLocale as any); // Type assertion ile hatayı gideriyoruz
        registerLocale('en', enLocale as any);

        // Mevcut dile göre varsayılan locale'i ayarla
        setDefaultLocale(i18n.language === 'tr' ? 'tr' : 'en');
    }, [i18n.language]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await reportService.getShiftReport(
                    startDate.toISOString().split('T')[0],
                    endDate.toISOString().split('T')[0]
                );
                const reportList = Array.isArray(data) ? data : [data];
                setReportData(reportList);
                setFilteredData(reportList);

                // Departman ve şube listelerini oluştur
                const uniqueDepartments = Array.from(
                    new Set(
                        reportList.flatMap(item =>
                            item.department?.map((dept: { name: string }) => dept.name) || []
                        )
                    )
                );
                const uniqueBranches = Array.from(
                    new Set(
                        reportList.flatMap(item =>
                            item.branch?.map((branch: { name: string }) => branch.name) || []
                        )
                    )
                );
                const uniqueUsers = Array.from(
                    new Set(
                        reportList.map(item => item.name)
                    )
                );
                setDepartments(uniqueDepartments);
                setBranches(uniqueBranches);
                setUsers(uniqueUsers);
            } catch (error: any) {
                handleApiError(error, t);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [startDate, endDate]);

    // Filtre değiştiğinde veriyi güncelle
    useEffect(() => {
        filterData();
    }, [selectedDepartment, selectedBranch, selectedUsers, reportData]);

    // Seçili kullanıcıların detaylarını ayarla
    useEffect(() => {
        if (selectedUsers.length > 0) {
            const usersDetails = selectedUsers
                .map(userName => reportData.find(user => user.name === userName))
                .filter(user => user !== undefined) as ShiftReportDto[];
            setSelectedUsersDetails(usersDetails);
        } else {
            setSelectedUsersDetails([]);
        }
    }, [selectedUsers, reportData]);

    // Çalışanlar için dakika değerlerini cache'le
    useEffect(() => {
        if (reportData.length > 0) {
            const newCache = new Map();
            reportData.forEach(employee => {
                // Backend'den gelen gerçek verileri kullan
                const lateTotal = employee.totalLateMinutes || 0;
                const earlyTotal = employee.totalEarlyLeaveMinutes || 0;
                const breakOverdraftTotal = employee.totalBreakOverdraftMinutes || 0;

                newCache.set(employee.userId, {
                    lateTotal: lateTotal,
                    earlyTotal: earlyTotal,
                    breakOverdraftTotal: breakOverdraftTotal
                });
            });
            setEmployeeMinutesCache(newCache);
        }
    }, [reportData]);

    // Filtreleme fonksiyonu
    const filterData = () => {
        let filtered = [...reportData];

        if (selectedDepartment) {
            filtered = filtered.filter(item =>
                item.department?.some(dept => dept.name === selectedDepartment)
            );
        }

        if (selectedBranch) {
            filtered = filtered.filter(item =>
                item.branch?.some(branch => branch.name === selectedBranch)
            );
        }

        if (selectedUsers.length > 0) {
            filtered = filtered.filter(item => selectedUsers.includes(item.name));
        }

        setFilteredData(filtered);
    };

    // Filtre sıfırlama
    const resetFilters = () => {
        setSelectedDepartment(undefined);
        setSelectedBranch(undefined);
        setSelectedUsers([]);
    };

    // Özet istatistikleri hesapla
    const calculateStats = () => {
        if (!filteredData.length) return {
            totalLate: 0,
            totalEarlyLeave: 0,
            totalAbsent: 0,
            totalLeave: 0,
            totalBreakOverdraft: 0,
            worstPerformer: '',
            bestPerformer: '',
            totalEmployees: 0,
            compliantEmployees: 0,
            complianceRate: 0,
            totalViolations: 0,
            departmentStats: {} as Record<string, { total: number, late: number, early: number, absent: number, leave: number, breakOverdraft: number, employees: number }>,
            branchStats: {} as Record<string, { total: number, late: number, early: number, absent: number, leave: number, breakOverdraft: number, employees: number }>,
            topPerformers: [] as Array<{ name: string, department: string, totalViolations: number }>
        };

        const stats = filteredData.reduce((acc, curr) => ({
            totalLate: acc.totalLate + curr.lateDays,
            totalEarlyLeave: acc.totalEarlyLeave + curr.earlyLeaveDays,
            totalAbsent: acc.totalAbsent + curr.absentDays,
            totalLeave: acc.totalLeave + curr.leaveDays,
            totalBreakOverdraft: acc.totalBreakOverdraft + curr.breakOverdraftDays,
        }), { totalLate: 0, totalEarlyLeave: 0, totalAbsent: 0, totalLeave: 0, totalBreakOverdraft: 0 });


        // ... existing calculations ...
        // Toplam vardiya ihlali sayısı (mola aşımı dahil)
        const totalViolations = stats.totalLate + stats.totalEarlyLeave + stats.totalAbsent + stats.totalBreakOverdraft;

        // En kötü performans gösteren çalışan (tüm ihlaller dahil)
        const worstPerformer = filteredData.reduce((a, b) =>
            (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays > 
             b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) ? a : b
        );

        // Performans sıralamasına göre en başarılı çalışanlar (en az ihlali olanlar)
        const sortedByPerformance = [...filteredData].sort((a, b) =>
            (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays) - 
            (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays)
        );

        const bestPerformer = sortedByPerformance[0];

        // En iyi performansa sahip çalışanlar listesi (en fazla ilk 10)
        const topPerformers = sortedByPerformance
            .slice(0, 10)
            .map(emp => ({
                name: emp.name,
                department: getDepartmentName(emp.department),
                totalViolations: emp.lateDays + emp.earlyLeaveDays + emp.absentDays + emp.breakOverdraftDays
            }));

        // Hiç ihlali olmayan çalışanlar (tüm kategoriler dahil)
        const compliantEmployees = filteredData.filter(emp =>
            emp.lateDays === 0 && emp.earlyLeaveDays === 0 && emp.absentDays === 0 && emp.breakOverdraftDays === 0
        );

        // Toplam çalışan sayısı
        const totalEmployees = filteredData.length;

        // Uyumlu çalışan sayısı ve oranı
        const complianceRate = totalEmployees > 0
            ? (compliantEmployees.length / totalEmployees) * 100
            : 0;
        // Departman bazlı istatistikler (yeni yapıya göre)
        const departmentStats: Record<string, { total: number, late: number, early: number, absent: number, leave: number, breakOverdraft: number, employees: number }> = {};
        filteredData.forEach(emp => {
            const deptNames = emp.department?.map(dept => dept.name) || ['Belirtilmemiş'];

            deptNames.forEach(deptName => {
                if (!departmentStats[deptName]) {
                    departmentStats[deptName] = {
                        total: 0,
                        late: 0,
                        early: 0,
                        absent: 0,
                        leave: 0,
                        breakOverdraft: 0,
                        employees: 0,
                    };
                }

                departmentStats[deptName].total += (emp.lateDays + emp.earlyLeaveDays + emp.absentDays + emp.breakOverdraftDays);
                departmentStats[deptName].late += emp.lateDays;
                departmentStats[deptName].early += emp.earlyLeaveDays;
                departmentStats[deptName].absent += emp.absentDays;
                departmentStats[deptName].leave += emp.leaveDays;
                departmentStats[deptName].breakOverdraft += emp.breakOverdraftDays;
                departmentStats[deptName].employees += 1;
            });
        });

        // Şube bazlı istatistikler (yeni yapıya göre)
        const branchStats: Record<string, { total: number, late: number, early: number, absent: number, leave: number, breakOverdraft: number, employees: number }> = {};
        filteredData.forEach(emp => {
            const branchNames = emp.branch?.map(branch => branch.name) || ['Belirtilmemiş'];

            branchNames.forEach(branchName => {
                if (!branchStats[branchName]) {
                    branchStats[branchName] = {
                        total: 0,
                        late: 0,
                        early: 0,
                        absent: 0,
                        leave: 0,
                        breakOverdraft: 0,
                        employees: 0,
                    };
                }

                branchStats[branchName].total += (emp.lateDays + emp.earlyLeaveDays + emp.absentDays + emp.breakOverdraftDays);
                branchStats[branchName].late += emp.lateDays;
                branchStats[branchName].early += emp.earlyLeaveDays;
                branchStats[branchName].absent += emp.absentDays;
                branchStats[branchName].leave += emp.leaveDays;
                branchStats[branchName].breakOverdraft += emp.breakOverdraftDays;
                branchStats[branchName].employees += 1;
            });
        });

        return {
            ...stats,
            worstPerformer: worstPerformer.name,
            bestPerformer: bestPerformer.name,
            totalEmployees: filteredData.length,
            compliantEmployees: compliantEmployees.length,
            complianceRate,
            totalViolations: stats.totalLate + stats.totalEarlyLeave + stats.totalAbsent + stats.totalBreakOverdraft,
            departmentStats,
            branchStats,
            topPerformers
        };
    };

    const stats = calculateStats();

    // Kıyaslama verilerini hesapla
    const comparisonData = useMemo(() => {
        if (selectedDepartmentsForComparison.length === 0 && selectedBranchesForComparison.length === 0) {
            return null;
        }

        // Karışık seçim kontrolü
        if (selectedDepartmentsForComparison.length > 0 && selectedBranchesForComparison.length > 0) {
            return null; // Karışık seçim yapılamaz
        }

        const comparisonItems: Array<{
            name: string;
            type: 'department' | 'branch';
            totalViolations: number;
            late: number;
            early: number;
            absent: number;
            leave: number;
            breakOverdraft: number;
            employees: number;
            complianceRate: number;
        }> = [];

        if (selectedDepartmentsForComparison.length > 0) {
            // Departman kıyaslaması
            selectedDepartmentsForComparison.forEach(deptName => {
                const deptEmployees = filteredData.filter(emp =>
                    emp.department?.some(dept => dept.name === deptName)
                );

                const deptStats = deptEmployees.reduce((acc, emp) => ({
                    late: acc.late + emp.lateDays,
                    early: acc.early + emp.earlyLeaveDays,
                    absent: acc.absent + emp.absentDays,
                    leave: acc.leave + emp.leaveDays,
                    breakOverdraft: acc.breakOverdraft + emp.breakOverdraftDays,
                }), { late: 0, early: 0, absent: 0, leave: 0, breakOverdraft: 0 });

                const totalViolations = deptStats.late + deptStats.early + deptStats.absent + deptStats.breakOverdraft;
                const compliantEmployees = deptEmployees.filter(emp =>
                    emp.lateDays === 0 && emp.earlyLeaveDays === 0 && emp.absentDays === 0 && emp.breakOverdraftDays === 0
                ).length;
                const complianceRate = deptEmployees.length > 0
                    ? (compliantEmployees / deptEmployees.length) * 100
                    : 0;

                comparisonItems.push({
                    name: deptName,
                    type: 'department',
                    totalViolations,
                    late: deptStats.late,
                    early: deptStats.early,
                    absent: deptStats.absent,
                    leave: deptStats.leave,
                    breakOverdraft: deptStats.breakOverdraft,
                    employees: deptEmployees.length,
                    complianceRate
                });
            });
        } else if (selectedBranchesForComparison.length > 0) {
            // Şube kıyaslaması
            selectedBranchesForComparison.forEach(branchName => {
                const branchEmployees = filteredData.filter(emp =>
                    emp.branch?.some(branch => branch.name === branchName)
                );

                const branchStats = branchEmployees.reduce((acc, emp) => ({
                    late: acc.late + emp.lateDays,
                    early: acc.early + emp.earlyLeaveDays,
                    absent: acc.absent + emp.absentDays,
                    leave: acc.leave + emp.leaveDays,
                    breakOverdraft: acc.breakOverdraft + emp.breakOverdraftDays,
                }), { late: 0, early: 0, absent: 0, leave: 0, breakOverdraft: 0 });

                const totalViolations = branchStats.late + branchStats.early + branchStats.absent + branchStats.breakOverdraft;
                const compliantEmployees = branchEmployees.filter(emp =>
                    emp.lateDays === 0 && emp.earlyLeaveDays === 0 && emp.absentDays === 0 && emp.breakOverdraftDays === 0
                ).length;
                const complianceRate = branchEmployees.length > 0
                    ? (compliantEmployees / branchEmployees.length) * 100
                    : 0;

                comparisonItems.push({
                    name: branchName,
                    type: 'branch',
                    totalViolations,
                    late: branchStats.late,
                    early: branchStats.early,
                    absent: branchStats.absent,
                    leave: branchStats.leave,
                    breakOverdraft: branchStats.breakOverdraft,
                    employees: branchEmployees.length,
                    complianceRate
                });
            });
        }

        return comparisonItems.length > 0 ? comparisonItems : null;
    }, [selectedDepartmentsForComparison, selectedBranchesForComparison, filteredData]);

    // İhlal dağılımını hesapla
    const violationDistribution = useMemo(() => {
        const total = stats.totalLate + stats.totalEarlyLeave + stats.totalAbsent + stats.totalBreakOverdraft;
        if (total === 0) return { late: 0, early: 0, absent: 0, leave: 0, breakOverdraft: 0 };

        return {
            late: parseFloat(((stats.totalLate / total) * 100).toFixed(1)),
            early: parseFloat(((stats.totalEarlyLeave / total) * 100).toFixed(1)),
            absent: parseFloat(((stats.totalAbsent / total) * 100).toFixed(1)),
            leave: parseFloat(((stats.totalLeave / total) * 100).toFixed(1)),
            breakOverdraft: parseFloat(((stats.totalBreakOverdraft / total) * 100).toFixed(1))
        };
    }, [stats]);


    // Departman ve şube verileri için hesaplamalar
    const departmentAnalysis = useMemo(() => {
        const departmentData = Object.keys(stats.departmentStats).map(dept => {
            // Bu departmandaki personelleri bul
            const deptEmployees = filteredData.filter(emp =>
                emp.department?.some(d => d.name === dept)
            );

            // Departman için toplam ihlaller
            const totalViolations = stats.departmentStats[dept].total;
            const lateCount = stats.departmentStats[dept].late;
            const earlyCount = stats.departmentStats[dept].early;
            const absentCount = stats.departmentStats[dept].absent;
            const employeeCount = stats.departmentStats[dept].employees;

            // Bu departmandaki personel listesi (ihlal sayılarına göre sıralı)
            const employeesList = deptEmployees
                .map(emp => ({
                    name: emp.name,
                    totalViolations: emp.lateDays + emp.earlyLeaveDays + emp.absentDays + emp.breakOverdraftDays,
                    lateDays: emp.lateDays,
                    earlyLeaveDays: emp.earlyLeaveDays,
                    absentDays: emp.absentDays,
                    leaveDays: emp.leaveDays,
                    breakOverdraftDays: emp.breakOverdraftDays
                }))
                .sort((a, b) => b.totalViolations - a.totalViolations);

            return {
                name: dept,
                totalViolations,
                lateCount,
                earlyCount,
                absentCount,
                employeeCount,
                employeesList
            };
        });

        return departmentData.sort((a, b) => b.totalViolations - a.totalViolations);
    }, [filteredData, stats]);

    const branchAnalysis = useMemo(() => {
        const branchData = Object.keys(stats.branchStats).map(branch => {
            // Bu şubedeki personelleri bul
            const branchEmployees = filteredData.filter(emp =>
                emp.branch?.some(b => b.name === branch)
            );

            // Şube için toplam ihlaller
            const totalViolations = stats.branchStats[branch].total;
            const lateCount = stats.branchStats[branch].late;
            const earlyCount = stats.branchStats[branch].early;
            const absentCount = stats.branchStats[branch].absent;
            const employeeCount = stats.branchStats[branch].employees;

            // Bu şubedeki personel listesi (ihlal sayılarına göre sıralı)
            const employeesList = branchEmployees
                .map(emp => ({
                    name: emp.name,
                    totalViolations: emp.lateDays + emp.earlyLeaveDays + emp.absentDays + emp.breakOverdraftDays,
                    lateDays: emp.lateDays,
                    earlyLeaveDays: emp.earlyLeaveDays,
                    absentDays: emp.absentDays,
                    leaveDays: emp.leaveDays,
                    breakOverdraftDays: emp.breakOverdraftDays
                }))
                .sort((a, b) => b.totalViolations - a.totalViolations);

            return {
                name: branch,
                totalViolations,
                lateCount,
                earlyCount,
                absentCount,
                employeeCount,
                employeesList
            };
        });

        return branchData.sort((a, b) => b.totalViolations - a.totalViolations);
    }, [filteredData, stats]);

    // Zaman bazlı ihlal trendi için veri hazırlama
    const timeSeriesData = useMemo(() => {
        // Departman ve şube bazlı en yüksek ihlale sahip 5 birim
        const topDepartments = departmentAnalysis.slice(0, 5);
        const topBranches = branchAnalysis.slice(0, 5);

        // Trendleri oluşturma
        const getDepartmentSeries = () => {
            return topDepartments?.map(dept => ({
                name: `${dept.name} (${t('shiftReport.department')})`,
                data: dept.totalViolations > 0 ? [
                    dept.totalViolations * 0.25, // Örnek trend verileri
                    dept.totalViolations * 0.4,
                    dept.totalViolations * 0.3,
                    dept.totalViolations * 0.55,
                    dept.totalViolations * 0.7,
                    dept.totalViolations * 0.75,
                    dept.totalViolations
                ] : [0, 0, 0, 0, 0, 0, 0],
                type: 'line'
            }));
        };

        const getBranchSeries = () => {
            return topBranches.map(branch => ({
                name: `${branch.name} (${t('shiftReport.branch')})`,
                data: branch.totalViolations > 0 ? [
                    branch.totalViolations * 0.2, // Örnek trend verileri
                    branch.totalViolations * 0.35,
                    branch.totalViolations * 0.4,
                    branch.totalViolations * 0.45,
                    branch.totalViolations * 0.6,
                    branch.totalViolations * 0.8,
                    branch.totalViolations
                ] : [0, 0, 0, 0, 0, 0, 0],
                type: 'line'
            }));
        };

        // Örnek tarih aralığı (son 7 gün)
        const dateLabels = [];
        const endDate = new Date();
        for (let i = 6; i >= 0; i--) {
            const date = new Date();
            date.setDate(endDate.getDate() - i);
            dateLabels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));
        }

        return {
            departmentSeries: getDepartmentSeries(),
            branchSeries: getBranchSeries(),
            allSeries: [...getDepartmentSeries(), ...getBranchSeries()],
            dateLabels
        };
    }, [departmentAnalysis, branchAnalysis]);

    // Trend line chart için ayarlar
    // Tablo için sütun tanımları
    const columns: ColumnsType<ShiftReportDto> = [
        {
            title: t('shiftReport.column.name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('shiftReport.column.department'),
            dataIndex: 'department',
            key: 'department',
            filters: departments?.map(dept => ({ text: dept, value: dept })),
            onFilter: (value, record) => record.department?.some(dept => dept.name === value),
            render: (department) => getDepartmentName(department),
        },
        {
            title: t('shiftReport.column.branch'),
            dataIndex: 'branch',
            key: 'branch',
            filters: branches.map(branch => ({ text: branch, value: branch })),
            onFilter: (value, record) => record.branch?.some(branch => branch.name === value),
            render: (branch) => getBranchName(branch),
        },
        {
            title: t('shiftReport.column.lateArrival'),
            dataIndex: 'lateDays',
            key: 'lateDays',
            sorter: (a, b) => a.lateDays - b.lateDays,
            render: (lateDays: number, record: ShiftReportDto) => (
                <OverlayTrigger
                    placement="top"
                    overlay={<BootstrapTooltip>{`${formatMinutesToHoursAndMinutes(record.totalLateMinutes || 0)} geç kaldı`}</BootstrapTooltip>}
                >
                    <Tag color="warning" style={{ fontSize: '14px', padding: '2px 10px', cursor: 'pointer' }}>
                        {lateDays} gün
                    </Tag>
                </OverlayTrigger>
            )
        },
        {
            title: t('shiftReport.column.earlyLeaving'),
            dataIndex: 'earlyLeaveDays',
            key: 'earlyLeaveDays',
            sorter: (a, b) => a.earlyLeaveDays - b.earlyLeaveDays,
            ellipsis: { showTitle: false },
            render: (earlyLeaveDays: number, record: ShiftReportDto) => (
                <OverlayTrigger
                    placement="top"
                    overlay={<BootstrapTooltip>{`${formatMinutesToHoursAndMinutes(record.totalEarlyLeaveMinutes || 0)} erken çıktı`}</BootstrapTooltip>}
                >
                    <Tag
                        color="error"
                        style={{ fontSize: '14px', padding: '2px 10px', cursor: 'pointer' }}
                    >
                        {earlyLeaveDays} gün
                    </Tag>
                </OverlayTrigger>
            ),
        },

        {
            title: t('shiftReport.column.absence'),
            dataIndex: 'absentDays',
            key: 'absentDays',
            sorter: (a, b) => a.absentDays - b.absentDays,
            render: (absentDays) => (
                <Tag color="processing" style={{ fontSize: '14px', padding: '2px 10px' }}>
                    {absentDays} gün
                </Tag>
            )
        },
        {
            title: 'İzin Günleri',
            dataIndex: 'leaveDays',
            key: 'leaveDays',
            sorter: (a, b) => a.leaveDays - b.leaveDays,
            render: (leaveDays: number, record: ShiftReportDto) => (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <BootstrapTooltip>
                            {record.leaveDaysList && record.leaveDaysList.length > 0
                                ? `İzin tarihleri: ${record.leaveDaysList.slice(0, 3).join(', ')}${record.leaveDaysList.length > 3 ? '...' : ''}`
                                : 'İzin kullanılmamış'
                            }
                        </BootstrapTooltip>
                    }
                >
                    <Tag color="cyan" style={{ fontSize: '14px', padding: '2px 10px', cursor: 'pointer' }}>
                        {leaveDays} gün
                    </Tag>
                </OverlayTrigger>
            )
        },
        {
            title: 'Mola Aşımı',
            dataIndex: 'breakOverdraftDays',
            key: 'breakOverdraftDays',
            sorter: (a, b) => a.breakOverdraftDays - b.breakOverdraftDays,
            render: (breakOverdraftDays: number, record: ShiftReportDto) => (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <BootstrapTooltip>
                            {`${formatMinutesToHoursAndMinutes(record.totalBreakOverdraftMinutes || 0)} mola aşımı`}
                        </BootstrapTooltip>
                    }
                >
                    <Tag color="purple" style={{ fontSize: '14px', padding: '2px 10px', cursor: 'pointer' }}>
                        {breakOverdraftDays} gün
                    </Tag>
                </OverlayTrigger>
            )
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Breadcrumb */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">Performans Raporu</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">{t('shiftReport.breadcrumb.reports')}</li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {t('shiftReport.breadcrumb.shiftReport')}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center">
                        <div className="me-2">
                            <div className="d-flex align-items-center">
                                <DatePicker
                                    locale={i18n.language === 'tr' ? 'tr' : 'en'} // Locale'i ayarla
                                    selected={startDate}
                                    onChange={(date: Date | null) => date && setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    className="form-control form-control-sm"
                                    dateFormat="dd/MM/yyyy"
                                />
                                <span className="mx-2">-</span>
                                <DatePicker
                                    locale={i18n.language === 'tr' ? 'tr' : 'en'} // Locale'i ayarla
                                    selected={endDate}
                                    onChange={(date: Date | null) => date && setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    className="form-control form-control-sm"
                                    dateFormat="dd/MM/yyyy"
                                />
                            </div>
                        </div>
                        <div className="mb-2">
                            {user?.permissions?.includes(PermissionType.EXPORT) && (
                            <div className="dropdown me-2"> 
                                <Link
                                    to="#"
                                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                    data-bs-toggle="dropdown"
                                >
                                    <i className="ti ti-file-export me-1" />
                                    {t('common.buttons.export')}
                                </Link>
                                <ul className="dropdown-menu dropdown-menu-end p-3">
                                    <li>
                                        <Link
                                            to="#"
                                            className="dropdown-item rounded-2 d-flex align-items-center py-2"
                                            onClick={() => {
                                                // Vardiya verileri için sütun tanımları
                                                                type ShiftDataType = {
                                                                    name: string;
                                                                    department: string;
                                                                    branch: string;
                                                                    lateDays: string;
                                                                    earlyLeaveDays: string;
                                                                    absentDays: string;
                                                                    leaveDays: string;
                                                                    breakOverdraftDays: string;
                                                                };

                                                                // PDF için sütun tanımları - Landscape A4 genişliğine göre optimize edildi (297mm, margin'lerle ~281mm)
                                                                const columnsForPdf = [
                                                                    { header: normalizeTurkishChars('Ad Soyad'), property: 'name' as keyof ShiftDataType, width: 50, align: 'left' as const },
                                                                    { header: normalizeTurkishChars('Departman'), property: 'department' as keyof ShiftDataType, width: 40, align: 'left' as const },
                                                                    { header: normalizeTurkishChars('Şube'), property: 'branch' as keyof ShiftDataType, width: 35, align: 'left' as const },
                                                                    { header: normalizeTurkishChars('Geç Gelme'), property: 'lateDays' as keyof ShiftDataType, width: 30, align: 'center' as const },
                                                                    { header: normalizeTurkishChars('Erken Çıkış'), property: 'earlyLeaveDays' as keyof ShiftDataType, width: 30, align: 'center' as const },
                                                                    { header: normalizeTurkishChars('Devamsızlık'), property: 'absentDays' as keyof ShiftDataType, width: 30, align: 'center' as const },
                                                                    { header: normalizeTurkishChars('İzin'), property: 'leaveDays' as keyof ShiftDataType, width: 25, align: 'center' as const },
                                                                    { header: normalizeTurkishChars('Mola Aşımı'), property: 'breakOverdraftDays' as keyof ShiftDataType, width: 30, align: 'center' as const }
                                                                ];

                                                                // PDF için formatlanmış veri
                                                                const formattedData = filteredData.map(employee => {
                                                                    return {
                                                                        name: normalizeTurkishChars(employee.name),
                                                                        department: normalizeTurkishChars(getDepartmentName(employee.department)),
                                                                        branch: normalizeTurkishChars(getBranchName(employee.branch)),
                                                                        lateDays: `${employee.lateDays} gün`,
                                                                        earlyLeaveDays: `${employee.earlyLeaveDays} gün`,
                                                                        absentDays: `${employee.absentDays} gün`,
                                                                        leaveDays: `${employee.leaveDays} gün`,
                                                                        breakOverdraftDays: `${employee.breakOverdraftDays} gün`
                                                                    };
                                                                });

                                                // Filtre açıklaması
                                                let description = normalizeTurkishChars('Performans Raporu');

                                                if (startDate && endDate) {
                                                    const startDateStr = startDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                                    const endDateStr = endDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                                    description += ` - Tarih: ${startDateStr} - ${endDateStr}`;
                                                }

                                                if (selectedDepartment) {
                                                    description += ` - Departman: ${normalizeTurkishChars(selectedDepartment)}`;
                                                }

                                                if (selectedBranch) {
                                                    description += ` - Şube: ${normalizeTurkishChars(selectedBranch)}`;
                                                }

                                                                // İstatistik bilgileri - Türkçe karakter desteği ile
                                                                const statistics = [
                                                                    {
                                                                        label: normalizeTurkishChars('Toplam Personel'),
                                                                        value: stats.totalEmployees.toString()
                                                                    },
                                                                    {
                                                                        label: normalizeTurkishChars('İhlalsiz Personel'),
                                                                        value: stats.compliantEmployees.toString()
                                                                    },
                                                                    {
                                                                        label: normalizeTurkishChars('Toplam Geç Gelme'),
                                                                        value: stats.totalLate.toString()
                                                                    },
                                                                    {
                                                                        label: normalizeTurkishChars('Toplam Erken Çıkış'),
                                                                        value: stats.totalEarlyLeave.toString()
                                                                    },
                                                                    {
                                                                        label: normalizeTurkishChars('Toplam Devamsızlık'),
                                                                        value: stats.totalAbsent.toString()
                                                                    },
                                                                    {
                                                                        label: normalizeTurkishChars('Toplam İzin'),
                                                                        value: stats.totalLeave.toString()
                                                                    },
                                                                    {
                                                                        label: normalizeTurkishChars('Toplam Mola Aşımı'),
                                                                        value: stats.totalBreakOverdraft.toString()
                                                                    }
                                                                ];

                                                // createPdf fonksiyonunu çağır
                                                createPdf(formattedData, {
                                                    title: normalizeTurkishChars('Performans Raporu'),
                                                    description: description,
                                                    headerColor: '#2c3e50', // Koyu lacivert
                                                    accentColor: '#3498db', // Mavi
                                                    columns: columnsForPdf,
                                                    showStatistics: true, // İstatistik kutularını göster
                                                    statistics: statistics,
                                                    footer: {
                                                        text: `© ${new Date().getFullYear()} ${normalizeTurkishChars('DINOX')} - ${normalizeTurkishChars('Tüm Hakları Saklıdır')}`,
                                                        showPageNumbers: true
                                                    }
                                                });
                                            }}
                                        >
                                            <i className="ti ti-file-type-pdf me-2 text-danger" />
                                            {t('common.buttons.pdf')}
                                        </Link>
                                    </li>
                                    <li className="mt-1">
                                        <Link
                                            to="#"
                                            className="dropdown-item rounded-2 d-flex align-items-center py-2"
                                            onClick={() => {
                                                // Excel için sütun tanımları
                                                                type ShiftExcelType = {
                                                                    name: string;
                                                                    department: string;
                                                                    branch: string;
                                                                    lateDays: number;
                                                                    earlyLeaveDays: number;
                                                                    absentDays: number;
                                                                    leaveDays: number;
                                                                    breakOverdraftDays: number;
                                                                    totalViolations: number;
                                                                    complianceRate: string;
                                                                };

                                                const columnsForExcel = [
                                                    { header: 'Ad Soyad', property: 'name' as keyof ShiftExcelType, width: 25 },
                                                    { header: 'Departman', property: 'department' as keyof ShiftExcelType, width: 20 },
                                                    { header: 'Şube', property: 'branch' as keyof ShiftExcelType, width: 20 },
                                                    { header: 'Geç Gelme (Gün)', property: 'lateDays' as keyof ShiftExcelType, width: 18 },
                                                    { header: 'Erken Çıkış (Gün)', property: 'earlyLeaveDays' as keyof ShiftExcelType, width: 18 },
                                                    { header: 'Devamsızlık (Gün)', property: 'absentDays' as keyof ShiftExcelType, width: 18 },
                                                    { header: 'İzin (Gün)', property: 'leaveDays' as keyof ShiftExcelType, width: 15 },
                                                    { header: 'Mola Aşımı (Gün)', property: 'breakOverdraftDays' as keyof ShiftExcelType, width: 18 },
                                                    { header: 'Toplam İhlal', property: 'totalViolations' as keyof ShiftExcelType, width: 15 },
                                                    { header: 'Uyum Oranı', property: 'complianceRate' as keyof ShiftExcelType, width: 15 }
                                                ];

                                                // Excel için formatlanmış veri
                                                const formattedData = filteredData.map(employee => {
                                                    const totalViolations = employee.lateDays + employee.earlyLeaveDays + employee.absentDays + employee.breakOverdraftDays;
                                                    // Varsayalım ki bir ayda 22 işgünü var
                                                    const workingDays = 22;
                                                    const complianceRate = ((workingDays - totalViolations) / workingDays * 100).toFixed(2) + '%';

                                                    return {
                                                        name: normalizeTurkishChars(employee.name),
                                                        department: getDepartmentName(employee.department),
                                                        branch: getBranchName(employee.branch),
                                                        lateDays: employee.lateDays,
                                                        earlyLeaveDays: employee.earlyLeaveDays,
                                                        absentDays: employee.absentDays,
                                                        leaveDays: employee.leaveDays,
                                                        breakOverdraftDays: employee.breakOverdraftDays,
                                                        totalViolations: totalViolations,
                                                        complianceRate: complianceRate
                                                    };
                                                });

                                                // Excel dosyasını oluştur
                                                exportToExcel(formattedData, 'Performans_Raporu', columnsForExcel);
                                            }}
                                        >
                                            <i className="ti ti-file-type-xls me-2 text-success" />
                                            {t('common.buttons.excel')}
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sayfa Geneli Filtreler */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="d-flex flex-wrap justify-content-between align-items-end">
                            <div className="d-flex flex-wrap gap-4 mb-3 mb-md-0">
                                <div>
                                    <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                        <i className="ti ti-building-skyscraper me-1 text-primary"></i>
                                        {t('shiftReport.departmentFilter')}
                                    </label>
                                    <Select
                                        placeholder={t('shiftReport.allDepartments')}
                                        allowClear
                                        style={{ width: 220, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                        onChange={(value) => setSelectedDepartment(value)}
                                        value={selectedDepartment}
                                        suffixIcon={<i className="ti ti-chevron-down text-primary"></i>}
                                        className="filter-select m-2"
                                        dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    >
                                        {departments?.map(dept => (
                                            <Option key={dept} value={dept}>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#1890ff" }}></span>
                                                    {dept}
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                        <i className="ti ti-map-pin me-1 text-danger"></i>
                                        {t('shiftReport.branchFilter')}
                                    </label>
                                    <Select
                                        placeholder={t('shiftReport.allBranches')}
                                        allowClear
                                        style={{ width: 220, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                        onChange={(value) => setSelectedBranch(value)}
                                        value={selectedBranch}
                                        suffixIcon={<i className="ti ti-chevron-down text-danger"></i>}
                                        className="filter-select m-2"
                                        dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    >
                                        {branches.map(branch => (
                                            <Option key={branch} value={branch}>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#ff4d4f" }}></span>
                                                    {branch}
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                        <i className="ti ti-user me-1 text-success"></i>
                                        Kişi Filtresi
                                    </label>
                                    <Select
                                        mode="multiple"
                                        placeholder={loading ? "Yükleniyor..." : `Kişi Seçin (${users.length})`}
                                        allowClear
                                        style={{
                                            width: 220,
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                                            minHeight: '32px',
                                            maxHeight: '60px'
                                        }}
                                        onChange={(value) => setSelectedUsers(value)}
                                        value={selectedUsers}
                                        suffixIcon={<i className="ti ti-chevron-down text-success"></i>}
                                        className="filter-select m-2"
                                        dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                        loading={loading}
                                        disabled={loading}
                                        maxTagCount={1}
                                        maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} kişi`}
                                        tagRender={(props) => {
                                            const { label, closable, onClose } = props;
                                            return (
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        backgroundColor: '#f6ffed',
                                                        border: '1px solid #b7eb8f',
                                                        borderRadius: '6px',
                                                        padding: '2px 8px',
                                                        margin: '2px',
                                                        fontSize: '12px',
                                                        maxWidth: '120px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    <span style={{ marginRight: '4px' }}>{label}</span>
                                                    {closable && (
                                                        <span
                                                            onClick={onClose}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '10px',
                                                                color: '#999',
                                                                marginLeft: 'auto'
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    )}
                                                </span>
                                            );
                                        }}
                                    >
                                        {users.map(user => (
                                            <Option key={user} value={user}>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#52c41a" }}></span>
                                                    {user}
                                                </div>
                                            </Option>
                                        ))}
                                        {users.length === 0 && (
                                            <Option disabled value="">
                                                <div className="text-muted">Kullanıcı bulunamadı</div>
                                            </Option>
                                        )}
                                    </Select>

                                    {/* Seçili kullanıcıları göster */}
                                    {/* {selectedUsers.length > 1 && (
                                        <div className="mt-1" style={{ width: 220, fontSize: '11px' }}>
                                            <div className="d-flex flex-wrap gap-1">
                                                {selectedUsers.slice(1).map(user => (
                                                    <span
                                                        key={user}
                                                        className="badge bg-light text-dark"
                                                        style={{
                                                            fontSize: '10px',
                                                            padding: '2px 6px',
                                                            border: '1px solid #e9ecef',
                                                            borderRadius: '12px',
                                                            maxWidth: '100px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => setSelectedUsers(prev => prev.filter(u => u !== user))}
                                                        title={`${user} - Kaldırmak için tıkla`}
                                                    >
                                                        {user.length > 12 ? user.substring(0, 12) + '...' : user}
                                                        <i className="ti ti-x ms-1" style={{ fontSize: '8px' }}></i>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )} */}
                                </div>
                            </div>
                            <div>
                                <Button
                                    type="default"
                                    icon={<FilterOutlined />}
                                    onClick={resetFilters}
                                    className="d-flex align-items-center"
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                                        background: 'white',
                                        border: '1px solid #e9ecef'
                                    }}
                                >
                                    <span className="ms-1">{t('shiftReport.resetFilters')}</span>
                                </Button>
                            </div>
                        </div>

                        {(selectedDepartment || selectedBranch || selectedUsers.length > 0) && (
                            <div className="mt-3 pt-2 border-top">
                                <div className="d-flex align-items-center flex-wrap">
                                    <small className="text-muted me-2">{t('shiftReport.activeFilters')}:</small>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {selectedDepartment && (
                                            <Tag
                                                color="blue"
                                                closable
                                                onClose={() => setSelectedDepartment(undefined)}
                                                style={{ borderRadius: '20px', padding: '2px 10px' }}
                                            >
                                                <i className="ti ti-building-skyscraper me-1"></i>
                                                {selectedDepartment}
                                            </Tag>
                                        )}
                                        {selectedBranch && (
                                            <Tag
                                                color="red"
                                                closable
                                                onClose={() => setSelectedBranch(undefined)}
                                                style={{ borderRadius: '20px', padding: '2px 10px' }}
                                            >
                                                <i className="ti ti-map-pin me-1"></i>
                                                {selectedBranch}
                                            </Tag>
                                        )}
                                        {selectedUsers.map(user => (
                                            <Tag
                                                key={user}
                                                color="green"
                                                closable
                                                onClose={() => setSelectedUsers(prev => prev.filter(u => u !== user))}
                                                style={{ borderRadius: '20px', padding: '2px 10px' }}
                                            >
                                                <i className="ti ti-user me-1"></i>
                                                {user}
                                            </Tag>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Seçili Kullanıcılar Detay Kartı */}
                {selectedUsersDetails.length > 0 && (
                    <div className="card mb-4" style={{ borderLeft: '4px solid #52c41a', position: 'relative', zIndex: 0 }}>
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div>
                                    <h6 className="mb-1 fw-bold">
                                        <i className="ti ti-users me-2 text-success"></i>
                                        Seçili Kullanıcılar ({selectedUsersDetails.length})
                                    </h6>
                                </div>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<i className="ti ti-x"></i>}
                                    onClick={() => setSelectedUsers([])}
                                    className="text-muted"
                                />
                            </div>

                            <div className="row g-3">
                                {selectedUsersDetails.map((user) => (
                                    <div key={user.userId} className="col-lg-6 col-xl-4">
                                        <div className="border rounded-3 p-3" style={{ backgroundColor: '#fafafa' }}>
                                            <div className="d-flex align-items-center mb-2">
                                                <div className="avatar avatar-sm me-2" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                                    <span className="avatar-text fw-bold text-success fs-12">
                                                        {user.name.charAt(0)}
                                                    </span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1 fw-bold fs-16">{user.name}</h6>
                                                    <small className="text-muted">{getDepartmentName(user.department)}</small>
                                                </div>
                                            </div>

                                            <div className="row g-2">
                                                <div className="col-4">
                                                    <Tooltip
                                                        zIndex={9999}
                                                        title={(() => {
                                                            const cachedMinutes = employeeMinutesCache.get(user.userId);
                                                            const totalLateMinutes = cachedMinutes?.lateTotal || 0;
                                                            if (totalLateMinutes > 0) {
                                                                const hours = Math.floor(totalLateMinutes / 60);
                                                                const minutes = totalLateMinutes % 60;
                                                                const timeText = hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
                                                                return `Toplam ${timeText} geç kaldı`;
                                                            }
                                                            return user.lateDays > 0 ? `${user.lateDays} gün geç geldi` : 'Hiç geç kalmadı';
                                                        })()}
                                                        placement="top"
                                                    >
                                                        <div className="text-center p-2 rounded-2 cursor-pointer" style={{ backgroundColor: '#fff2e8' }}>
                                                            <div className="fs-18 text-warning fw-bold">{user.lateDays}</div>
                                                            <div className="fs-12 text-muted">Geç</div>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                                <div className="col-4">
                                                    <Tooltip
                                                        zIndex={9999}
                                                        title={(() => {
                                                            const cachedMinutes = employeeMinutesCache.get(user.userId);
                                                            const totalEarlyMinutes = cachedMinutes?.earlyTotal || 0;
                                                            if (totalEarlyMinutes > 0) {
                                                                const hours = Math.floor(totalEarlyMinutes / 60);
                                                                const minutes = totalEarlyMinutes % 60;
                                                                const timeText = hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
                                                                return `Toplam ${timeText} erken çıktı`;
                                                            }
                                                            return user.earlyLeaveDays > 0 ? `${user.earlyLeaveDays} gün erken çıktı` : 'Hiç erken çıkmadı';
                                                        })()}
                                                        placement="top"
                                                    >
                                                        <div className="text-center p-2 rounded-2 cursor-pointer" style={{ backgroundColor: '#fff1f0' }}>
                                                            <div className="fs-18 text-danger fw-bold">{user.earlyLeaveDays}</div>
                                                            <div className="fs-12 text-muted">Erken</div>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                                <div className="col-4">
                                                    <Tooltip
                                                        zIndex={9999}
                                                        title={user.absentDays > 0 ? `${user.absentDays} gün devamsızlık yaptı` : 'Hiç devamsızlık yapmadı'}
                                                        placement="top"
                                                    >
                                                        <div className="text-center p-2 rounded-2 cursor-pointer" style={{ backgroundColor: '#e6f7ff' }}>
                                                            <div className="fs-18 text-black fw-bold">{user.absentDays}</div>
                                                            <div className="fs-12 text-muted">Devamsız</div>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                                <div className="col-4">
                                                    <Tooltip
                                                        zIndex={9999}
                                                        title={user.leaveDays > 0 ? `${user.leaveDays} gün izin kullandı` : 'Hiç izin kullanmadı'}
                                                        placement="top"
                                                    >
                                                        <div className="text-center p-2 rounded-2 cursor-pointer" style={{ backgroundColor: '#e6fffb' }}>
                                                            <div className="fs-18 text-cyan fw-bold">{user.leaveDays}</div>
                                                            <div className="fs-12 text-muted">İzin</div>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                                <div className="col-4">
                                                    <Tooltip
                                                        zIndex={9999}
                                                        title={(() => {
                                                            const cachedMinutes = employeeMinutesCache.get(user.userId);
                                                            const totalBreakOverdraftMinutes = cachedMinutes?.breakOverdraftTotal || 0;
                                                            if (totalBreakOverdraftMinutes > 0) {
                                                                const hours = Math.floor(totalBreakOverdraftMinutes / 60);
                                                                const minutes = totalBreakOverdraftMinutes % 60;
                                                                const timeText = hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
                                                                return `Toplam ${timeText} mola aşımı`;
                                                            }
                                                            return user.breakOverdraftDays > 0 ? `${user.breakOverdraftDays} gün mola aşımı` : 'Hiç mola aşımı yok';
                                                        })()}
                                                        placement="top"
                                                    >
                                                        <div className="text-center p-2 rounded-2 cursor-pointer" style={{ backgroundColor: '#f9f0ff' }}>
                                                            <div className="fs-18 text-purple fw-bold">{user.breakOverdraftDays}</div>
                                                            <div className="fs-12 text-muted">Mola</div>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                                {/* Uyum yüzdesi - Negatif değerler çıkabileceği için kaldırıldı */}
                                                {/* <div className="col-4">
                                                    <Tooltip
                                                        title={(() => {
                                                            const totalViolations = user.lateDays + user.earlyLeaveDays + user.absentDays + user.breakOverdraftDays;
                                                            const complianceRate = (((22 - totalViolations) / 22) * 100).toFixed(1);
                                                            return `${22 - totalViolations}/22 gün uyumlu (${complianceRate}%)`;
                                                        })()}
                                                        placement="top"
                                                    >
                                                        <div className="text-center p-2 rounded-2 cursor-pointer" style={{ backgroundColor: '#f6ffed' }}>
                                                            <div className="fs-18 text-success fw-bold">
                                                                {(((22 - (user.lateDays + user.earlyLeaveDays + user.absentDays + user.breakOverdraftDays)) / 22) * 100).toFixed(0)}%
                                                            </div>
                                                            <div className="fs-12 text-muted">Uyum</div>
                                                        </div>
                                                    </Tooltip>
                                                </div> */}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('shiftReport.loading')}</span>
                        </div>
                    </div>
                ) : filteredData.length > 0 ? (
                    <>
                        <div className="row">
                            {/* Ana Özet Kartları - Geliştirilmiş */}
                            {/* Genel Performans Oranı Kartı - Negatif değerler çıkabileceği için kaldırıldı */}
                            {/* <div className="col-xl-3 col-md-6 d-flex">
                                <div className="card flex-fill hover-border-primary" style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.07)', borderRadius: '10px', transition: 'all 0.3s ease' }}>
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h6 className="card-title mb-0 fw-semibold">Genel Performans Oranı</h6>
                                                <small className="text-muted">{t('shiftReport.cards.complianceRate')}</small>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <h1 className="mb-2 fw-bold" style={{ fontSize: '2.5rem', background: 'linear-gradient(45deg, #07A35A, #00D68F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                                {stats.complianceRate.toFixed(1)}%
                                            </h1>
                                            <div className="d-flex align-items-center">
                                                <Progress
                                                    percent={stats.complianceRate}
                                                    showInfo={false}
                                                    status={stats.complianceRate > 70 ? "success" : stats.complianceRate > 40 ? "normal" : "exception"}
                                                    className="mb-0 flex-fill"
                                                    strokeWidth={8}
                                                    strokeColor={{
                                                        '0%': '#10B981',
                                                        '100%': '#34D399',
                                                    }}
                                                />
                                            </div>
                                            <div className="d-flex mt-2 justify-content-between">
                                                <small className="text-muted fw-semibold">{stats.compliantEmployees} / {stats.totalEmployees} {t('shiftReport.cards.employees')}</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div> */}
                            <div className="col-xl-4 col-md-4 d-flex">
                                <div className="card flex-fill hover-border-success" style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.07)', borderRadius: '10px', transition: 'all 0.3s ease', position: 'relative', zIndex: 0 }}>
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h6 className="card-title mb-0 fw-semibold">{t('shiftReport.cards.highestPerformance')}</h6>
                                                {/* <small className="text-muted">{t('shiftReport.cards.lowestPerformance')}</small> */}
                                            </div>
                                            <div className="avatar avatar-md bg-success-subtle" style={{ borderRadius: '12px' }}>
                                                <CheckCircleOutlined className="text-success fs-18" />
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            {stats.totalEmployees < 2 ? (
                                                <div className="text-center py-4">
                                                    <h6 className="text-muted mb-0">
                                                        Karşılaştırma için en az 2 çalışan seçiniz.
                                                    </h6>
                                                </div>
                                            ) : stats.topPerformers.length > 0 ? (
                                                <>
                                                    <div className="text-center py-2">
                                                        <div className="avatar avatar-lg mb-3 mx-auto" style={{ backgroundColor: '#DCFCE7', border: '2px solid #86EFAC', borderRadius: '15px' }}>
                                                            <span className="avatar-text fw-bold text-success fs-22">{stats.bestPerformer.charAt(0)}</span>
                                                        </div>
                                                        <h4 className="fw-bold mb-1" style={{ color: '#16A34A' }}>{stats.bestPerformer}</h4>
                                                        <div className="d-flex justify-content-center align-items-center mb-2">
                                                            <div className="badge bg-light text-dark p-2">
                                                                {(() => {
                                                                    const employee = filteredData.find(emp => emp.name === stats.bestPerformer);
                                                                    return employee ?
                                                                        (employee.lateDays + employee.earlyLeaveDays + employee.absentDays + employee.breakOverdraftDays) :
                                                                        0;
                                                                })()} {t('shiftReport.cards.totalViolations')}
                                                            </div>
                                                        </div>

                                                        {stats.topPerformers.length > 1 && (
                                                            <div className="d-flex justify-content-center align-items-center">
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Popover id="popover-top-performers">
                                                                            <Popover.Header as="h3">{t('shiftReport.cards.topPerformers')}</Popover.Header>
                                                                            <Popover.Body>
                                                                                <ul className="ps-3 mb-0">
                                                                                    {stats.topPerformers
                                                                                        .filter((_, idx) => idx > 0) // İlk eleman hariç
                                                                                        .map((emp, idx) => (
                                                                                            <li key={idx} className="mb-1">
                                                                                                {emp.name} - {emp.department} ({emp.totalViolations} {t('shiftReport.cards.violations')})
                                                                                            </li>
                                                                                        ))
                                                                                    }
                                                                                </ul>
                                                                            </Popover.Body>
                                                                        </Popover>
                                                                    }
                                                                >
                                                                    <div className="badge bg-success px-3 py-2 rounded-pill" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                                                                        <span className="me-1">+ {stats.topPerformers.length - 1} {t('shiftReport.cards.topPerformers')}</span>
                                                                        <i className="ti ti-info-circle"></i>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <div className="avatar avatar-lg mb-3 mx-auto" style={{ backgroundColor: '#FEF2F2', border: '2px solid #FECACA', borderRadius: '15px' }}>
                                                        <CloseCircleOutlined className="text-danger fs-18" />
                                                    </div>
                                                    <h6 className="text-danger mb-0">{t('shiftReport.cards.noDataFound')}</h6>
                                                    <p className="text-muted mt-2 mb-0">{t('shiftReport.cards.noDataFound')}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-4 col-md-4 d-flex">
                                <div className="card flex-fill hover-border-danger" style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.07)', borderRadius: '10px', transition: 'all 0.3s ease', position: 'relative', zIndex: 0 }}>
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h6 className="card-title mb-0 fw-semibold">{t('shiftReport.cards.lowestPerformance')}</h6>
                                                {/* <small className="text-muted">{t('shiftReport.cards.lowestPerformance')}</small> */}
                                            </div>
                                            <div className="avatar avatar-md bg-danger-subtle" style={{ borderRadius: '12px' }}>
                                                <CloseCircleOutlined className="text-danger fs-18" />
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            {stats.totalEmployees < 2 ? (
                                                <div className="text-center py-4">
                                                    <h6 className="text-muted mb-0">
                                                        Karşılaştırma için en az 2 çalışan seçiniz.
                                                    </h6>
                                                </div>
                                            ) : (
                                                <div className="text-center py-2">
                                                    <div className="avatar avatar-lg mb-3 mx-auto" style={{ backgroundColor: '#FEE2E2', border: '2px solid #FCA5A5', borderRadius: '15px' }}>
                                                        <span className="avatar-text fw-bold text-danger fs-22">{stats.worstPerformer.charAt(0)}</span>
                                                    </div>
                                                    <h4 className="fw-bold mb-1" style={{ color: '#B91C1C' }}>{stats.worstPerformer}</h4>
                                                    <div className="d-flex justify-content-center align-items-center flex-wrap">
                                                        <Tag color="error" className="mx-1 mb-1" style={{ fontSize: '11px' }}>{
                                                            filteredData.find(emp => emp.name === stats.worstPerformer)?.lateDays || 0
                                                        } {t('shiftReport.cards.late')}</Tag>
                                                        <Tag color="warning" className="mx-1 mb-1" style={{ fontSize: '11px' }}>{
                                                            filteredData.find(emp => emp.name === stats.worstPerformer)?.earlyLeaveDays || 0
                                                        } Erken</Tag>
                                                        <Tag color="processing" className="mx-1 mb-1" style={{ fontSize: '11px' }}>{
                                                            filteredData.find(emp => emp.name === stats.worstPerformer)?.absentDays || 0
                                                        } {t('shiftReport.cards.absence')}</Tag>
                                                        <Tag color="cyan" className="mx-1 mb-1" style={{ fontSize: '11px' }}>{
                                                            filteredData.find(emp => emp.name === stats.worstPerformer)?.leaveDays || 0
                                                        } İzin</Tag>
                                                        <Tag color="purple" className="mx-1 mb-1" style={{ fontSize: '11px' }}>{
                                                            filteredData.find(emp => emp.name === stats.worstPerformer)?.breakOverdraftDays || 0
                                                        } Mola</Tag>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-4 col-md-4 d-flex">
                                <div className="card flex-fill hover-border-info" style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.07)', borderRadius: '10px', transition: 'all 0.3s ease', position: 'relative', zIndex: 0 }}>
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h6 className="card-title mb-0 fw-semibold">{t('shiftReport.cards.violationDistribution')}</h6>
                                                {/* <small className="text-muted">{t('shiftReport.cards.violationDistribution')}</small> */}
                                            </div>
                                            <div className="avatar avatar-md bg-info-subtle" style={{ borderRadius: '12px' }}>
                                                <InfoCircleOutlined className="text-info fs-18" />
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <div className="row g-2" style={{ height: '180px' }}>
                                                <div className="col text-center">
                                                    <div className="d-flex flex-column h-100">
                                                        <small className="fw-semibold text-warning mb-2">{violationDistribution.late}%</small>
                                                        <div className="flex-grow-1 d-flex align-items-end justify-content-center">
                                                            <div 
                                                                style={{ 
                                                                    width: '20px', 
                                                                    height: `${Math.max(violationDistribution.late * 1, 10)}%`,
                                                                    background: 'linear-gradient(to top, #faad14, #ffcc00)',
                                                                    borderRadius: '10px 10px 3px 3px',
                                                                    boxShadow: '0 2px 4px rgba(250, 173, 20, 0.3)'
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <small className="fw-semibold mt-2" style={{ fontSize: '10px' }}>Geç</small>
                                                    </div>
                                                </div>
                                                <div className="col text-center">
                                                    <div className="d-flex flex-column h-100">
                                                        <small className="fw-semibold text-danger mb-2">{violationDistribution.early}%</small>
                                                        <div className="flex-grow-1 d-flex align-items-end justify-content-center">
                                                            <div 
                                                                style={{ 
                                                                    width: '20px', 
                                                                    height: `${Math.max(violationDistribution.early * 1, 10)}%`,
                                                                    background: 'linear-gradient(to top, #ff4d4f, #ff7875)',
                                                                    borderRadius: '10px 10px 3px 3px',
                                                                    boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)'
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <small className="fw-semibold mt-2" style={{ fontSize: '10px' }}>Erken</small>
                                                    </div>
                                                </div>
                                                <div className="col text-center">
                                                    <div className="d-flex flex-column h-100">
                                                        <small style={{ fontSize: '12px'}} className="fw-semibold text-primary mb-2">{violationDistribution.absent}%</small>
                                                        <div className="flex-grow-1 d-flex align-items-end justify-content-center">
                                                            <div 
                                                                style={{ 
                                                                    width: '20px', 
                                                                    height: `${Math.max(violationDistribution.absent * 1, 10)}%`,
                                                                    background: 'linear-gradient(to top, #1890ff, #69c0ff)',
                                                                    borderRadius: '10px 10px 3px 3px',
                                                                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)'
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <small className="fw-semibold mt-2" style={{ fontSize: '10px' }}>Devamsız</small>
                                                    </div>
                                                </div>
                                                <div className="col text-center">
                                                    <div className="d-flex flex-column h-100">
                                                        <small className="fw-semibold text-info mb-2">{violationDistribution.leave}%</small>
                                                        <div className="flex-grow-1 d-flex align-items-end justify-content-center">
                                                            <div 
                                                                style={{ 
                                                                    width: '20px', 
                                                                    height: `${Math.max(violationDistribution.leave * 1, 10)}%`,
                                                                    background: 'linear-gradient(to top, #13c2c2, #36cfc9)',
                                                                    borderRadius: '10px 10px 3px 3px',
                                                                    boxShadow: '0 2px 4px rgba(19, 194, 194, 0.3)'
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <small className="fw-semibold mt-2" style={{ fontSize: '10px' }}>İzin</small>
                                                    </div>
                                                </div>
                                                <div className="col text-center">
                                                    <div className="d-flex flex-column h-100">
                                                        <small className="fw-semibold text-purple mb-2">{violationDistribution.breakOverdraft}%</small>
                                                        <div className="flex-grow-1 d-flex align-items-end justify-content-center">
                                                            <div 
                                                                style={{ 
                                                                    width: '20px', 
                                                                    height: `${Math.max(violationDistribution.breakOverdraft * 1, 10)}%`,
                                                                    background: 'linear-gradient(to top, #722ed1, #b37feb)',
                                                                    borderRadius: '10px 10px 3px 3px',
                                                                    boxShadow: '0 2px 4px rgba(114, 46, 209, 0.3)'
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <small className="fw-semibold mt-2" style={{ fontSize: '10px' }}>Mola</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* İhlal Trendi Line Chart */}
                            <div className="col-12">
                                <div className="card" style={{ position: 'relative', zIndex: 1 }}>
                                    <div className="card-header">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="card-title mb-0">{t('shiftReport.analysis.personelViolationAnalysis')}</h5>
                                            <small className="text-muted">{t('shiftReport.analysis.personelViolationAnalysis')}</small>
                                        </div>
                                    </div>
                                    <div className="card-body" style={{ overflow: 'visible' }}>
                                        <div className="row mb-4">
                                            <div className="col-md-12">
                                                <div style={{ minHeight: "400px", overflow: "visible", position: "relative" }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                                        <h6 className="fw-semibold m-0">{t('shiftReport.analysis.violationTypesDistribution')}</h6>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="d-flex align-items-center">
                                                                <span className="d-inline-block me-1 rounded" style={{ width: "10px", height: "10px", backgroundColor: "#FF9B42" }}></span>
                                                                <span className="fs-13">{t('shiftReport.analysis.lateArrival')}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <span className="d-inline-block me-1 rounded" style={{ width: "10px", height: "10px", backgroundColor: "#E15759" }}></span>
                                                                <span className="fs-13">{t('shiftReport.analysis.earlyLeaving')}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <span className="d-inline-block me-1 rounded" style={{ width: "10px", height: "10px", backgroundColor: "#4E79A7" }}></span>
                                                                <span className="fs-13">{t('shiftReport.analysis.absence')}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <span className="d-inline-block me-1 rounded" style={{ width: "10px", height: "10px", backgroundColor: "#13C2C2" }}></span>
                                                                <span className="fs-13">İzin</span>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <span className="d-inline-block me-1 rounded" style={{ width: "10px", height: "10px", backgroundColor: "#722ED1" }}></span>
                                                                <span className="fs-13">Mola Aşımı</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div style={{ position: 'relative', zIndex: 100 }}>
                                                        <ReactApexChart
                                                            options={{
                                                                chart: {
                                                                    type: 'bar',
                                                                    stacked: true,
                                                                    height: 500,
                                                                    toolbar: {
                                                                        show: true,
                                                                        tools: {
                                                                            download: true,
                                                                            selection: false,
                                                                        zoom: false,
                                                                        zoomin: false,
                                                                        zoomout: false,
                                                                        pan: false,
                                                                        reset: false
                                                                    }
                                                                },
                                                                animations: {
                                                                    enabled: true,
                                                                    speed: 800,
                                                                    dynamicAnimation: {
                                                                        enabled: true,
                                                                        speed: 350
                                                                    }
                                                                },
                                                                fontFamily: 'inherit',
                                                                background: 'transparent'
                                                            },
                                                            plotOptions: {
                                                                bar: {
                                                                    horizontal: true,
                                                                    barHeight: filteredData.length > 5 ? '60%' : '20%',
                                                                    borderRadius: 6,
                                                                    distributed: false,
                                                                    columnWidth: '70%',
                                                                    dataLabels: {
                                                                        position: 'center'
                                                                    }
                                                                }
                                                            },
                                                            colors: ['#FF9B42', '#E15759', '#4E79A7', '#13C2C2', '#722ED1'],
                                                            dataLabels: {
                                                                enabled: true,
                                                                formatter: function (val: number): string {
                                                                    if (val <= 0) return '';
                                                                    return `${val}`;
                                                                },
                                                                style: {
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    colors: ['#fff']
                                                                },
                                                                dropShadow: {
                                                                    enabled: true,
                                                                    color: '#000',
                                                                    top: 0,
                                                                    left: 0,
                                                                    blur: 3,
                                                                    opacity: 0.2
                                                                }
                                                            },
                                                            stroke: {
                                                                width: 2,
                                                                colors: ['#fff']
                                                            },
                                                            xaxis: {
                                                                categories: filteredData
                                                                    .sort((a, b) =>
                                                                        (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) -
                                                                        (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays)
                                                                    )
                                                                    .slice(0, 10)
                                                                    .map(emp => emp.name),
                                                                labels: {
                                                                    formatter: function (val: string): string {
                                                                        return val.length > 15 ? val.substring(0, 15) + '...' : val;
                                                                    },
                                                                    style: {
                                                                        fontSize: '12px',
                                                                        fontWeight: 500
                                                                    }
                                                                },
                                                                axisBorder: {
                                                                    show: false
                                                                },
                                                                axisTicks: {
                                                                    show: false
                                                                }
                                                            },
                                                            yaxis: {
                                                                labels: {
                                                                    style: {
                                                                        fontSize: '12px'
                                                                    }
                                                                }
                                                            },
                                                            grid: {
                                                                borderColor: '#f1f1f1',
                                                                padding: {
                                                                    top: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    left: 10
                                                                },
                                                                xaxis: {
                                                                    lines: {
                                                                        show: false
                                                                    }
                                                                }
                                                            },
                                                            tooltip: {
                                                                shared: true,
                                                                intersect: false,
                                                                followCursor: true,
                                                                style: {
                                                                    fontSize: '13px'
                                                                },
                                                                x: {
                                                                    formatter: function (val: any): string {
                                                                        // Değeri string'e çevir
                                                                        const value = String(val);
                                                                        // Personel isminden departman ve şube bilgisini bul
                                                                        const personel = filteredData.find(emp => emp.name === value);
                                                                        if (personel) {
                                                                            return `<div style="padding: 8px 0">
                                                                                <strong>${value}</strong><br/>
                                                                                <span style="font-size: 12px; opacity: 0.7">
                                                                                    ${personel.department.map((dep: any) => dep.name).join(', ')} / ${personel.branch.map((branch: any) => branch.name).join(', ')}
                                                                                </span>
                                                                            </div>`;
                                                                        }
                                                                        return value;
                                                                    }
                                                                },
                                                                y: {
                                                                    formatter: function (val: number, opts: any): string {
                                                                        const seriesIndex = opts.seriesIndex;
                                                                        const seriesName = opts.w.globals.seriesNames[seriesIndex];
                                                                        const dataPointIndex = opts.dataPointIndex;

                                                                        // Personel bilgisini bul
                                                                        const sortedData = filteredData
                                                                            .sort((a, b) =>
                                                                                (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) -
                                                                                (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays)
                                                                            )
                                                                            .slice(0, 10);

                                                                        const employee = sortedData[dataPointIndex];

                                                                        if (employee && val > 0) {
                                                                            let additionalInfo = '';

                                                                            // Cache'den çalışanın dakika bilgilerini al
                                                                            const cachedMinutes = employeeMinutesCache.get(employee.userId);

                                                                            // Seriye göre ek bilgi ekle
                                                                            if (seriesIndex === 0 && employee.lateDays > 0) { // Geç Gelme
                                                                                const totalLateMinutes = cachedMinutes?.lateTotal || 0;
                                                                                if (totalLateMinutes > 0) {
                                                                                    const hours = Math.floor(totalLateMinutes / 60);
                                                                                    const minutes = totalLateMinutes % 60;
                                                                                    const timeText = hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
                                                                                    additionalInfo = `<br/><span style="opacity: 0.8; font-size: 11px; color: #fa8c16;">Toplam ${timeText} geç</span>`;
                                                                                }
                                                                            } else if (seriesIndex === 1 && employee.earlyLeaveDays > 0) { // Erken Çıkış
                                                                                const totalEarlyMinutes = cachedMinutes?.earlyTotal || 0;
                                                                                if (totalEarlyMinutes > 0) {
                                                                                    const hours = Math.floor(totalEarlyMinutes / 60);
                                                                                    const minutes = totalEarlyMinutes % 60;
                                                                                    const timeText = hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
                                                                                    additionalInfo = `<br/><span style="opacity: 0.8; font-size: 11px; color: #ff4d4f;">Toplam ${timeText} erken</span>`;
                                                                                }
                                                                            } else if (seriesIndex === 2 && employee.absentDays > 0) { // Devamsızlık
                                                                                additionalInfo = `<br/><span style="opacity: 0.8; font-size: 11px; color: #1890ff;">Toplam ${val} gün devamsız</span>`;
                                                                            } else if (seriesIndex === 3 && employee.leaveDays > 0) { // İzin
                                                                                additionalInfo = `<br/><span style="opacity: 0.8; font-size: 11px; color: #13C2C2;">Toplam ${val} gün izin</span>`;
                                                                            } else if (seriesIndex === 4 && employee.breakOverdraftDays > 0) { // Mola Aşımı
                                                                                const totalBreakOverdraftMinutes = cachedMinutes?.breakOverdraftTotal || 0;
                                                                                if (totalBreakOverdraftMinutes > 0) {
                                                                                    const hours = Math.floor(totalBreakOverdraftMinutes / 60);
                                                                                    const minutes = totalBreakOverdraftMinutes % 60;
                                                                                    const timeText = hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
                                                                                    additionalInfo = `<br/><span style="opacity: 0.8; font-size: 11px; color: #722ED1;">Toplam ${timeText} mola aşımı</span>`;
                                                                                }
                                                                            }

                                                                            return `<strong>${val} gün ${seriesName}</strong>${additionalInfo}`;
                                                                        }

                                                                        return val > 0 ? `<strong>${val} gün ${seriesName}</strong>` : '';
                                                                    }
                                                                },
                                                                marker: {
                                                                    show: false
                                                                }
                                                            },
                                                            legend: {
                                                                show: false
                                                            },
                                                            fill: {
                                                                opacity: 1,
                                                                type: 'gradient',
                                                                gradient: {
                                                                    shade: 'dark',
                                                                    type: 'horizontal',
                                                                    shadeIntensity: 0.25,
                                                                    gradientToColors: undefined,
                                                                    inverseColors: true,
                                                                    opacityFrom: 0.85,
                                                                    opacityTo: 0.85,
                                                                    stops: [0, 100]
                                                                }
                                                            },
                                                            theme: {
                                                                mode: 'light',
                                                                monochrome: {
                                                                    enabled: false
                                                                }
                                                            }
                                                        }}
                                                        series={[
                                                            {
                                                                name: t('shiftReport.analysis.lateArrival'),
                                                                data: filteredData
                                                                    .sort((a, b) =>
                                                                        (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) -
                                                                        (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays)
                                                                    )
                                                                    .slice(0, 10)
                                                                    .map(emp => emp.lateDays)
                                                            },
                                                            {
                                                                name: t('shiftReport.analysis.earlyLeaving'),
                                                                data: filteredData
                                                                    .sort((a, b) =>
                                                                        (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) -
                                                                        (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays)
                                                                    )
                                                                    .slice(0, 10)
                                                                    .map(emp => emp.earlyLeaveDays)
                                                            },
                                                            {
                                                                name: t('shiftReport.analysis.absence'),
                                                                data: filteredData
                                                                    .sort((a, b) =>
                                                                        (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) -
                                                                        (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays)
                                                                    )
                                                                    .slice(0, 10)
                                                                    .map(emp => emp.absentDays)
                                                            },
                                                            {
                                                                name: 'İzin',
                                                                data: filteredData
                                                                    .sort((a, b) =>
                                                                        (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) -
                                                                        (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays)
                                                                    )
                                                                    .slice(0, 10)
                                                                    .map(emp => emp.leaveDays)
                                                            },
                                                            {
                                                                name: 'Mola Aşımı',
                                                                data: filteredData
                                                                    .sort((a, b) =>
                                                                        (b.lateDays + b.earlyLeaveDays + b.absentDays + b.breakOverdraftDays) -
                                                                        (a.lateDays + a.earlyLeaveDays + a.absentDays + a.breakOverdraftDays)
                                                                    )
                                                                    .slice(0, 10)
                                                                    .map(emp => emp.breakOverdraftDays)
                                                            }
                                                        ]}
                                                        type="bar"
                                                        height={350}
                                                    />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-lg-12 col-md-12">
                                                <div className="mb-4">
                                                    <div className="card h-100 border-0 bg-white shadow-sm" style={{ borderRadius: '12px', position: 'relative', zIndex: 0 }}>
                                                        <div className="card-body p-4">
                                                            <h6 className="fw-semibold mb-3">{t('shiftReport.analysis.violationAnalysis')}</h6>

                                                            <p className="text-muted fs-13 mb-4">
                                                                {t('shiftReport.analysis.violationAnalysisDescription')}
                                                            </p>

                                                            <div className="d-flex align-items-center gap-2 mb-4">
                                                                <div className="p-2 rounded-3 bg-primary-subtle text-primary">
                                                                    <i className="ti ti-info-circle"></i>
                                                                </div>
                                                                <p className="fs-13 mb-0">
                                                                    {t('shiftReport.analysis.violationAnalysisGraphDescription')}
                                                                </p>
                                                            </div>

                                                            <div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>

                                        <div className="col-md-12">
                                            <div className="alert alert-info mt-2 mb-0 shadow-sm rounded-3" style={{ backgroundColor: '#e6f7ff', border: 'none', borderLeft: '4px solid #1890ff' }}>
                                                <div className="d-flex">
                                                    <InfoCircleOutlined className="me-3 fs-18" style={{ color: '#1890ff' }} />
                                                    <div>
                                                        <p className="mb-0 fs-13">
                                                            {t('shiftReport.analysis.violationAnalysisGraphLegend')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Detay Tablosu - Ant Design Table */}
                        <div className="col-12">
                            <div className="card" style={{ position: 'relative', zIndex: 0 }}>
                                <div className="card-header">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="card-title mb-0">{t('shiftReport.personelShiftPerformance')}</h5>

                                        <div className="dataTables_length">
                                            <label className="d-flex align-items-center text-muted">
                                                <span className="me-2">Sayfa Başına</span>
                                                <select
                                                    className="form-select form-select-sm"
                                                    style={{ width: "70px" }}
                                                    value={pageSize}
                                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                                >
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="50">50</option>
                                                    <option value="100">100</option>
                                                </select>
                                                <span className="ms-2">kayıt</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <Table
                                        columns={columns}
                                        dataSource={filteredData}
                                        rowKey="userId"
                                        pagination={{
                                            pageSize: pageSize,
                                            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`
                                        }}
                                        bordered
                                        size="middle"
                                        loading={loading}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="alert alert-info">
                        {t('shiftReport.noDataFound')}
                    </div>
                )}

                {/* Kıyaslama Bölümü */}
                <div className="card mb-4" style={{ borderLeft: '4px solid #1890ff' }}>
                    <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div>
                                <h6 className="mb-1 fw-bold">
                                    <i className="ti ti-chart-bar me-2 text-primary"></i>
                                    Departman ve Şube Kıyaslaması
                                </h6>
                                <small className="text-muted">Seçtiğiniz departmanlar veya şubeler arasında performans karşılaştırması yapabilirsiniz</small>
                            </div>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                    <i className="ti ti-building-skyscraper me-1 text-primary"></i>
                                    Departman Kıyaslaması
                                </label>
                                <Select
                                    mode="multiple"
                                    placeholder="Kıyaslamak için departman seçin"
                                    allowClear
                                    style={{ width: '100%', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                    onChange={(value) => {
                                        setSelectedDepartmentsForComparison(value);
                                        if (value.length > 0) {
                                            setSelectedBranchesForComparison([]);
                                        }
                                    }}
                                    value={selectedDepartmentsForComparison}
                                    disabled={selectedBranchesForComparison.length > 0}
                                    suffixIcon={<i className="ti ti-chevron-down text-primary"></i>}
                                    dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    maxTagCount={2}
                                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} departman`}
                                >
                                    {departments?.map(dept => (
                                        <Option key={dept} value={dept}>
                                            <div className="d-flex align-items-center">
                                                <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#1890ff" }}></span>
                                                {dept}
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                    <i className="ti ti-map-pin me-1 text-danger"></i>
                                    Şube Kıyaslaması
                                </label>
                                <Select
                                    mode="multiple"
                                    placeholder="Kıyaslamak için şube seçin"
                                    allowClear
                                    style={{ width: '100%', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                    onChange={(value) => {
                                        setSelectedBranchesForComparison(value);
                                        if (value.length > 0) {
                                            setSelectedDepartmentsForComparison([]);
                                        }
                                    }}
                                    value={selectedBranchesForComparison}
                                    disabled={selectedDepartmentsForComparison.length > 0}
                                    suffixIcon={<i className="ti ti-chevron-down text-danger"></i>}
                                    dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    maxTagCount={2}
                                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} şube`}
                                >
                                    {branches.map(branch => (
                                        <Option key={branch} value={branch}>
                                            <div className="d-flex align-items-center">
                                                <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#ff4d4f" }}></span>
                                                {branch}
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        {/* Karışık seçim uyarısı */}
                        {selectedDepartmentsForComparison.length > 0 && selectedBranchesForComparison.length > 0 && (
                            <div className="alert alert-warning mb-3" role="alert">
                                <i className="ti ti-alert-triangle me-2"></i>
                                Bir şube ile bir departmanı kıyaslamak mantıksızdır. Lütfen sadece departmanlar veya sadece şubeler seçin.
                            </div>
                        )}

                        {/* Kıyaslama Sonuçları */}
                        {comparisonData && comparisonData.length > 0 && (
                            <div className="mt-4">
                                <h6 className="mb-3 fw-semibold">
                                    <i className="ti ti-chart-line me-2"></i>
                                    Kıyaslama Sonuçları
                                </h6>
                                
                                {/* Kıyaslama Grafiği */}
                                <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                    <div className="card-body">
                                        <ReactApexChart
                                            options={{
                                                chart: {
                                                    type: 'bar',
                                                    height: 350,
                                                    toolbar: { show: true },
                                                    animations: { enabled: true, speed: 800 }
                                                },
                                                plotOptions: {
                                                    bar: {
                                                        horizontal: false,
                                                        columnWidth: '60%',
                                                        borderRadius: 6,
                                                        dataLabels: { position: 'top' }
                                                    }
                                                },
                                                dataLabels: {
                                                    enabled: true,
                                                    formatter: (val: number) => val > 0 ? `${val}` : '',
                                                    style: { fontSize: '12px', fontWeight: 600, colors: ['#000'] }
                                                },
                                                xaxis: {
                                                    categories: comparisonData.map(item => item.name),
                                                    labels: { style: { fontSize: '12px' } }
                                                },
                                                yaxis: {
                                                    title: { text: 'İhlal Sayısı' },
                                                    labels: { style: { fontSize: '12px' } }
                                                },
                                                colors: ['#FF9B42', '#E15759', '#4E79A7', '#13C2C2', '#722ED1'],
                                                legend: {
                                                    position: 'top',
                                                    horizontalAlign: 'right',
                                                    fontSize: '12px'
                                                },
                                                tooltip: {
                                                    y: { formatter: (val: number) => `${val} gün` }
                                                },
                                                grid: { borderColor: '#e7e7e7' }
                                            }}
                                            series={[
                                                {
                                                    name: 'Geç Gelme',
                                                    data: comparisonData.map(item => item.late)
                                                },
                                                {
                                                    name: 'Erken Çıkış',
                                                    data: comparisonData.map(item => item.early)
                                                },
                                                {
                                                    name: 'Devamsızlık',
                                                    data: comparisonData.map(item => item.absent)
                                                },
                                                {
                                                    name: 'İzin',
                                                    data: comparisonData.map(item => item.leave)
                                                },
                                                {
                                                    name: 'Mola Aşımı',
                                                    data: comparisonData.map(item => item.breakOverdraft)
                                                }
                                            ]}
                                            type="bar"
                                            height={350}
                                        />
                                    </div>
                                </div>

                                {/* Kıyaslama Tablosu */}
                                <div className="table-responsive">
                                    <Table
                                        dataSource={comparisonData.map((item, index) => ({ ...item, key: index }))}
                                        columns={[
                                            {
                                                title: comparisonData[0]?.type === 'department' ? 'Departman' : 'Şube',
                                                dataIndex: 'name',
                                                key: 'name',
                                                render: (text: string) => (
                                                    <strong style={{ color: comparisonData[0]?.type === 'department' ? '#1890ff' : '#ff4d4f' }}>
                                                        {text}
                                                    </strong>
                                                )
                                            },
                                            {
                                                title: 'Çalışan Sayısı',
                                                dataIndex: 'employees',
                                                key: 'employees',
                                                align: 'center' as const,
                                                sorter: (a, b) => a.employees - b.employees
                                            },
                                            {
                                                title: 'Geç Gelme',
                                                dataIndex: 'late',
                                                key: 'late',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="warning">{val} gün</Tag>,
                                                sorter: (a, b) => a.late - b.late
                                            },
                                            {
                                                title: 'Erken Çıkış',
                                                dataIndex: 'early',
                                                key: 'early',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="error">{val} gün</Tag>,
                                                sorter: (a, b) => a.early - b.early
                                            },
                                            {
                                                title: 'Devamsızlık',
                                                dataIndex: 'absent',
                                                key: 'absent',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="processing">{val} gün</Tag>,
                                                sorter: (a, b) => a.absent - b.absent
                                            },
                                            {
                                                title: 'İzin',
                                                dataIndex: 'leave',
                                                key: 'leave',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="cyan">{val} gün</Tag>,
                                                sorter: (a, b) => a.leave - b.leave
                                            },
                                            {
                                                title: 'Mola Aşımı',
                                                dataIndex: 'breakOverdraft',
                                                key: 'breakOverdraft',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="purple">{val} gün</Tag>,
                                                sorter: (a, b) => a.breakOverdraft - b.breakOverdraft
                                            },
                                            {
                                                title: 'Toplam İhlal',
                                                dataIndex: 'totalViolations',
                                                key: 'totalViolations',
                                                align: 'center' as const,
                                                render: (val: number) => (
                                                    <strong style={{ fontSize: '16px', color: val > 50 ? '#ff4d4f' : val > 20 ? '#faad14' : '#52c41a' }}>
                                                        {val}
                                                    </strong>
                                                ),
                                                sorter: (a, b) => a.totalViolations - b.totalViolations,
                                                defaultSortOrder: 'descend' as const
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                <FooterSection />
            </div>
        </div>
    );
};

export default ShiftReport; 